import { Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type Room, type Booking, type RecurringBooking } from '../lib/api'
import { useAuthStore } from '../lib/auth'
import { formatLocalDate } from '../lib/utils'
import {
  Calendar,
  Clock,
  MapPin,
  X,
  Pencil,
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  Shield,
  Check,
  Ban,
  CreditCard,
  Plus,
  Repeat,
  Trash2,
} from 'lucide-react'
import { useState, useEffect, useMemo } from 'react'
import { DashboardLayout } from '../components/DashboardLayout'
import { BookingDetailModal } from '../components/BookingDetailModal'
import { FilterTabs } from '../components/FilterTabs'
import { RecurringSeriesPanel } from '../components/RecurringSeriesPanel'
import { PageTabs } from '../components/PageTabs'
import { subscribeToSSE } from '../components/NotificationBell'

type BookingStatus = 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'denied'

// Unified booking item type for display
type UnifiedBooking = {
  id: string
  type: 'one-time' | 'recurring-occurrence'
  title: string
  roomId: string
  status?: BookingStatus
  startTime?: string
  endTime?: string
  notes?: string | null
  sortKey: number
  // Booking transparency fields
  bookingSource?: 'guest_request' | 'admin_direct'
  bookedBy?: {
    id: string
    name: string
    email: string
    phone?: string | null
    role?: string
    accountType?: string
  } | null
  createdBy?: { id: string; name: string; email: string } | null
  approvedBy?: { id: string; name: string; email: string } | null
  statusNote?: string | null
  statusChangedAt?: string | null
  // Ownership tracking
  userId?: string
  isRedacted?: boolean
  // Payment fields
  totalPrice?: number
  paymentStatus?: 'not_required' | 'pending' | 'paid' | 'refunded' | 'failed' | null
  // Recurring fields
  recurringBookingId?: string
  patternType?: string
}

export function BookingsPage() {
  const { currentOrg, memberships, user } = useAuthStore()
  const queryClient = useQueryClient()
  const [cancellingId, setCancellingId] = useState<string | null>(null)
  const [filter, setFilter] = useState<
    'all' | 'pending' | 'upcoming' | 'past' | 'payment_due' | 'cancelled' | 'recurring'
  >('upcoming')

  // Check URL for payment success and verify the payment
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const paymentStatus = params.get('payment')
    const bookingId = params.get('booking')

    if (paymentStatus === 'success' && bookingId) {
      // Verify the payment with the server
      api.stripe
        .verifyPayment(bookingId)
        .then(() => {
          // Refresh bookings to show updated status
          queryClient.invalidateQueries({ queryKey: ['bookings'] })
          // Clean up URL
          window.history.replaceState({}, '', '/dashboard/bookings')
        })
        .catch((err) => {
          console.error('Failed to verify payment:', err)
        })
    }

    // Returning from a voluntary donation checkout
    const donationStatus = params.get('donation')
    if (donationStatus === 'success' && bookingId) {
      api.stripe
        .verifyDonation(bookingId)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['bookings'] })
          window.history.replaceState({}, '', '/dashboard/bookings')
        })
        .catch((err) => {
          console.error('Failed to verify donation:', err)
        })
    } else if (donationStatus === 'cancelled') {
      // The booking stands either way; the guest can donate later from its details
      window.history.replaceState({}, '', '/dashboard/bookings')
    }
  }, [queryClient])

  // Check URL for filter parameter and booking ID
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const urlFilter = params.get('filter')
    if (urlFilter === 'pending') {
      setFilter('pending')
    } else if (urlFilter === 'past') {
      setFilter('past')
    } else if (urlFilter === 'all') {
      setFilter('all')
    } else if (urlFilter === 'payment_due') {
      setFilter('payment_due')
    } else if (urlFilter === 'cancelled') {
      setFilter('cancelled')
    }
  }, [])

  // Auto-open booking modal if booking ID is in URL (for notification deep links)
  const [pendingBookingId, setPendingBookingId] = useState<string | null>(null)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const bookingIdFromUrl = params.get('open')
    if (bookingIdFromUrl) {
      setPendingBookingId(bookingIdFromUrl)
      // Clean up URL without reloading
      window.history.replaceState({}, '', '/dashboard/bookings')
    }
  }, [])

  // Check if current user is admin or approver
  const currentMembership = memberships.find((m) => m.orgSlug === currentOrg?.orgSlug)
  const canApprove = currentMembership?.role === 'admin' || currentMembership?.role === 'approver'
  const isGuest = currentMembership?.role === 'guest' || currentMembership?.role === 'special_guest'

  // This page is the manage-side bookings list. Guests (and legacy email links)
  // belong on the personal My Bookings page - keep the query string so deep links
  // like ?open= and ?payment=success still work there.
  const managesAnyOrg = memberships.some((m) => m.role === 'admin' || m.role === 'approver')
  useEffect(() => {
    if (memberships.length > 0 && !managesAnyOrg) {
      window.location.replace('/my-bookings' + window.location.search)
    }
  }, [memberships.length, managesAnyOrg])

  // Modal state for booking detail view
  const [selectedBookingForDetail, setSelectedBookingForDetail] = useState<Booking | null>(null)

  // Approval state for bookings
  const [approvingId, setApprovingId] = useState<string | null>(null)
  const [denyingId, setDenyingId] = useState<string | null>(null)
  const [denyReason, setDenyReason] = useState('')

  // Edit state for bookings
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null)
  const [editBookingForm, setEditBookingForm] = useState({
    title: '',
    date: '',
    startTime: '',
    endTime: '',
    notes: '',
  })

  useEffect(() => {
    if (!currentOrg) {
      window.location.href = '/dashboard'
    }
  }, [currentOrg])

  // Subscribe to SSE for real-time updates when new bookings come in
  useEffect(() => {
    const unsubNotification = subscribeToSSE(
      'notification',
      (data: { notificationType?: string }) => {
        // Reload bookings when we get a booking-related notification
        if (
          data.notificationType === 'booking_request' ||
          data.notificationType === 'booking_approved' ||
          data.notificationType === 'booking_rejected' ||
          data.notificationType === 'payment_confirmed' ||
          data.notificationType === 'payment_received' ||
          data.notificationType === 'payment_refunded'
        ) {
          queryClient.invalidateQueries({ queryKey: ['bookings'] })
        }
      }
    )

    // Also listen for access_request events (from bookings.ts when approval needed)
    const unsubAccessRequest = subscribeToSSE('access_request', () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
    })

    // Listen for booking_update events (status changes, payment updates)
    const unsubBookingUpdate = subscribeToSSE('booking_update', () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
    })

    return () => {
      unsubNotification()
      unsubAccessRequest()
      unsubBookingUpdate()
    }
  }, [queryClient])

  const { data: bookingsData, isLoading } = useQuery({
    queryKey: ['bookings', currentOrg?.orgId],
    queryFn: async () => {
      if (!currentOrg) return { bookings: [] }
      // Don't pass myBookingsOnly - backend handles this based on user role
      return api.bookings.list({ includeCancelled: true })
    },
    enabled: !!currentOrg,
  })

  const bookings = bookingsData?.bookings || []

  const { data: roomsData } = useQuery({
    queryKey: ['rooms', currentOrg?.orgId],
    queryFn: async () => {
      if (!currentOrg) return { rooms: [] }
      return api.rooms.list()
    },
    enabled: !!currentOrg,
  })

  const rooms = roomsData?.rooms || []

  // Fetch recurring bookings (admin only)
  const { data: recurringData } = useQuery({
    queryKey: ['recurringBookings', currentOrg?.orgId],
    queryFn: async () => {
      return api.bookings.listRecurring()
    },
    enabled: !!currentOrg && canApprove,
  })

  const recurringBookings = recurringData?.recurringBookings || []

  // Fetch recurring occurrences for the upcoming period (admin only)
  const { data: occurrencesData } = useQuery({
    queryKey: ['recurringOccurrences', currentOrg?.orgId],
    queryFn: async () => {
      const now = new Date()
      const futureDate = new Date(now)
      futureDate.setDate(now.getDate() + 90) // 90 days ahead
      return api.bookings.getRecurringOccurrences({
        start: now.toISOString(),
        end: futureDate.toISOString(),
      })
    },
    enabled: !!currentOrg && canApprove,
  })

  const recurringOccurrences = occurrencesData?.occurrences || []

  const cancelMutation = useMutation({
    mutationFn: async ({ bookingId, isPending }: { bookingId: string; isPending: boolean }) => {
      if (!currentOrg) throw new Error('No organization selected')
      // For pending bookings, use DELETE to actually remove them
      // For confirmed bookings, use PUT to set status to cancelled
      if (isPending) {
        return api.bookings.delete(bookingId)
      } else {
        return api.bookings.update(bookingId, { status: 'cancelled' })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      setCancellingId(null)
    },
  })

  // Update mutations
  const updateBookingMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: { title?: string; startTime?: string; endTime?: string; notes?: string }
    }) => {
      if (!currentOrg) throw new Error('No organization selected')
      return api.bookings.update(id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      setEditingBooking(null)
    },
  })

  // Approve booking mutation
  const approveMutation = useMutation({
    mutationFn: async (bookingId: string) => {
      if (!currentOrg) throw new Error('No organization selected')
      return api.bookings.update(bookingId, { status: 'confirmed' })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      setApprovingId(null)
    },
  })

  // Deny booking mutation
  const denyMutation = useMutation({
    mutationFn: async ({ bookingId, reason }: { bookingId: string; reason?: string }) => {
      if (!currentOrg) throw new Error('No organization selected')
      return api.bookings.update(bookingId, { status: 'denied', statusNote: reason })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      setDenyingId(null)
      setDenyReason('')
    },
  })

  // Delete recurring booking mutation
  const deleteRecurringMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.bookings.deleteRecurring(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurringBookings'] })
    },
  })

  // Toggle recurring booking active/inactive
  const toggleRecurringMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      return api.bookings.updateRecurring(id, { isActive })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurringBookings'] })
    },
  })

  const [deletingRecurringId, setDeletingRecurringId] = useState<string | null>(null)

  // Recurring edit state
  const [editingRecurring, setEditingRecurring] = useState<RecurringBooking | null>(null)
  const [recurringEditForm, setRecurringEditForm] = useState({
    title: '',
    notes: '',
    startTime: '',
    endTime: '',
    roomId: '',
    endDate: '',
    isActive: true,
  })

  const updateRecurringMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Parameters<typeof api.bookings.updateRecurring>[1]
    }) => {
      return api.bookings.updateRecurring(id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurringBookings'] })
      queryClient.invalidateQueries({ queryKey: ['recurringOccurrences'] })
      setEditingRecurring(null)
    },
  })

  const openEditRecurring = (rb: RecurringBooking) => {
    setRecurringEditForm({
      title: rb.title || '',
      notes: rb.notes || '',
      startTime: rb.startTime,
      endTime: rb.endTime,
      roomId: rb.roomId,
      endDate: rb.endDate || '',
      isActive: rb.isActive,
    })
    setEditingRecurring(rb)
  }

  // Edit handlers
  const openEditBooking = (booking: Booking) => {
    const startDate = new Date(booking.startTime)
    const endDate = new Date(booking.endTime)
    setEditBookingForm({
      title: booking.title || '',
      date: formatLocalDate(startDate),
      startTime: startDate.toTimeString().slice(0, 5),
      endTime: endDate.toTimeString().slice(0, 5),
      notes: booking.notes || '',
    })
    setEditingBooking(booking)
  }

  const handleSaveBooking = () => {
    if (!editingBooking) return
    const startTime = new Date(`${editBookingForm.date}T${editBookingForm.startTime}`).toISOString()
    const endTime = new Date(`${editBookingForm.date}T${editBookingForm.endTime}`).toISOString()
    updateBookingMutation.mutate({
      id: editingBooking.id,
      data: {
        title: editBookingForm.title || undefined,
        startTime,
        endTime,
        notes: editBookingForm.notes || undefined,
      },
    })
  }

  const getRoomDetails = (roomId: string) => {
    return rooms.find((r: Room) => r.id === roomId)
  }

  // Create the unified bookings list from regular bookings + recurring occurrences
  const unifiedBookings: UnifiedBooking[] = [
    ...bookings.map(
      (b: Booking): UnifiedBooking => ({
        id: b.id,
        type: 'one-time',
        title: b.title || 'Booking',
        roomId: b.roomId,
        status: b.status,
        startTime: b.startTime,
        endTime: b.endTime,
        notes: b.notes,
        sortKey: new Date(b.startTime).getTime(),
        // Booking transparency fields
        bookingSource: b.bookingSource,
        bookedBy: b.bookedBy,
        createdBy: b.createdBy,
        approvedBy: b.approvedBy,
        statusNote: b.statusNote,
        statusChangedAt: b.statusChangedAt,
        // Ownership tracking
        userId: b.userId,
        isRedacted: b.isRedacted,
        // Payment fields
        totalPrice: b.totalPrice,
        paymentStatus: b.paymentStatus,
      })
    ),
    ...recurringOccurrences.map(
      (occ): UnifiedBooking => ({
        id: occ.id,
        type: 'recurring-occurrence',
        title: occ.title || 'Recurring Booking',
        roomId: occ.roomId,
        status: 'confirmed' as BookingStatus,
        startTime: occ.startTime,
        endTime: occ.endTime,
        sortKey: new Date(occ.startTime).getTime(),
        bookingSource: 'admin_direct',
        recurringBookingId: occ.recurringBookingId,
        patternType: occ.patternType,
      })
    ),
  ].sort((a: UnifiedBooking, b: UnifiedBooking) => {
    // Put the next scheduled booking at the top: upcoming bookings soonest-first,
    // then past bookings most-recent-first. "Past" uses end time to match the tab filter.
    const now = Date.now()
    const aEnd = a.endTime ? new Date(a.endTime).getTime() : a.sortKey
    const bEnd = b.endTime ? new Date(b.endTime).getTime() : b.sortKey
    const aUpcoming = aEnd >= now
    const bUpcoming = bEnd >= now
    if (aUpcoming !== bUpcoming) return aUpcoming ? -1 : 1
    return aUpcoming ? a.sortKey - b.sortKey : b.sortKey - a.sortKey
  })

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return {
      date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
    }
  }

  if (!currentOrg) return null

  const loading = isLoading

  // Count pending bookings for the badge
  const pendingCount = bookings.filter((b: Booking) => b.status === 'pending').length

  // Helper to check if a booking is in the past
  const isBookingPast = (booking: UnifiedBooking) => {
    return booking.endTime ? new Date(booking.endTime) < new Date() : false
  }

  // Count past bookings
  const pastCount = unifiedBookings.filter(isBookingPast).length

  // Count payment due bookings (pending payment status on confirmed/approved bookings)
  const paymentDueCount = bookings.filter(
    (b: Booking) =>
      b.paymentStatus === 'pending' && (b.status === 'confirmed' || b.status === 'pending')
  ).length

  // Count cancelled/denied bookings (including refunded)
  const cancelledCount = bookings.filter(
    (b: Booking) => b.status === 'cancelled' || b.status === 'denied'
  ).length

  // Auto-open booking modal when data is loaded and we have a pending booking ID
  useEffect(() => {
    if (pendingBookingId && bookings.length > 0) {
      const bookingToOpen = bookings.find((b: Booking) => b.id === pendingBookingId)
      if (bookingToOpen) {
        setSelectedBookingForDetail(bookingToOpen)
      }
      setPendingBookingId(null)
    }
  }, [pendingBookingId, bookings])

  // Filter bookings based on selected filter
  const filteredUnifiedBookings =
    filter === 'recurring'
      ? [] // Recurring tab renders its own content
      : filter === 'pending'
        ? unifiedBookings.filter((b) => b.status === 'pending')
        : filter === 'past'
          ? unifiedBookings.filter(
              (b) => isBookingPast(b) && b.status !== 'cancelled' && b.status !== 'denied'
            )
          : filter === 'upcoming'
            ? unifiedBookings.filter((b) => !isBookingPast(b) && b.status === 'confirmed')
            : filter === 'payment_due'
              ? unifiedBookings.filter(
                  (b) =>
                    b.paymentStatus === 'pending' &&
                    (b.status === 'confirmed' || b.status === 'pending')
                )
              : filter === 'cancelled'
                ? unifiedBookings.filter((b) => b.status === 'cancelled' || b.status === 'denied')
                : unifiedBookings.filter((b) => b.status !== 'cancelled' && b.status !== 'denied') // 'all' excludes cancelled

  // Helper to format recurring pattern description
  const formatRecurringPattern = (rb: RecurringBooking) => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    if (rb.patternType === 'weekly' && rb.dayOfWeek != null) {
      const interval = rb.weekInterval || 1
      return interval === 2
        ? `Every other ${dayNames[rb.dayOfWeek]}`
        : `Every ${dayNames[rb.dayOfWeek]}`
    } else if (rb.patternType === 'monthly_day' && rb.dayOfMonth != null) {
      const suffix =
        rb.dayOfMonth === 1 ? 'st' : rb.dayOfMonth === 2 ? 'nd' : rb.dayOfMonth === 3 ? 'rd' : 'th'
      return `Monthly on the ${rb.dayOfMonth}${suffix}`
    } else if (
      rb.patternType === 'monthly_week' &&
      rb.weekOfMonth != null &&
      rb.dayOfWeek != null
    ) {
      const weekNames = ['', '1st', '2nd', '3rd', '4th', 'Last']
      return `${weekNames[rb.weekOfMonth]} ${dayNames[rb.dayOfWeek]} of each month`
    }
    return 'Recurring'
  }

  const filterOptions = useMemo(() => {
    const options = [{ value: 'upcoming', label: 'Upcoming' }]
    if (canApprove) {
      options.push({
        value: 'pending',
        label: 'Pending',
        count: pendingCount,
        countColor: 'amber' as const,
      })
    }
    options.push(
      {
        value: 'payment_due',
        label: 'Payment',
        count: paymentDueCount,
        countColor: 'red' as const,
      },
      { value: 'past', label: 'Past', count: pastCount, countColor: 'gray' as const },
      {
        value: 'cancelled',
        label: 'Cancelled',
        count: cancelledCount,
        countColor: 'gray' as const,
      },
      { value: 'all', label: 'All' }
    )
    if (canApprove && recurringBookings.length > 0) {
      options.push({
        value: 'recurring',
        label: 'Recurring',
        count: recurringBookings.filter((rb) => rb.isActive).length,
        countColor: 'blue' as const,
      })
    }
    return options
  }, [canApprove, pendingCount, paymentDueCount, pastCount, cancelledCount, recurringBookings])

  return (
    <DashboardLayout title="Bookings" subtitle="Bookings and requests for your organisation">
      {currentMembership?.role === 'admin' && (
        <PageTabs
          tabs={[
            { label: 'Bookings', to: '/dashboard/bookings' },
            { label: 'Requests', to: '/dashboard/requests' },
          ]}
          active="/dashboard/bookings"
        />
      )}
      {/* Admin/Approver: inline Direct Booking action */}
      {canApprove && (
        <div className="mb-4">
          <Link
            to="/dashboard"
            search={{ action: 'newBooking' }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Direct Booking
          </Link>
        </div>
      )}
      <FilterTabs
        options={filterOptions}
        value={filter}
        onChange={(value) => setFilter(value as typeof filter)}
      />
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      )}

      {!loading && filter !== 'recurring' && filteredUnifiedBookings.length === 0 && (
        <div className="text-center py-8 sm:py-12">
          <Calendar className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
            {filter === 'pending'
              ? 'No pending approvals'
              : filter === 'past'
                ? 'No past bookings'
                : filter === 'payment_due'
                  ? 'No payments due'
                  : filter === 'upcoming'
                    ? 'No upcoming bookings'
                    : 'No bookings yet'}
          </h3>
          <p className="text-sm sm:text-base text-gray-500 mb-4">
            {filter === 'pending'
              ? 'All booking requests have been processed.'
              : filter === 'past'
                ? 'Your booking history will appear here.'
                : filter === 'payment_due'
                  ? 'All payments are up to date.'
                  : 'Book a room from the calendar to get started.'}
          </p>
          {filter === 'pending' || filter === 'past' || filter === 'payment_due' ? (
            <button
              onClick={() => setFilter('upcoming')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
            >
              View Upcoming Bookings
            </button>
          ) : (
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Calendar className="w-4 h-4" />
              Browse Rooms
            </Link>
          )}
        </div>
      )}

      {/* Recurring bookings tab content */}
      {!loading && filter === 'recurring' && <RecurringSeriesPanel materializedOnly />}
      {!loading && filter === 'recurring' && (
        <div className="space-y-4">
          {recurringBookings.length === 0 ? null : (
            recurringBookings.filter((booking) => !booking.materialized).map((rb) => {
              const room = getRoomDetails(rb.roomId)
              const activeExceptions = rb.exceptions?.length || 0
              return (
                <div
                  key={`recurring-${rb.id}`}
                  className={`bg-white rounded-lg border-2 shadow-sm overflow-hidden ${
                    rb.isActive ? 'border-blue-200' : 'border-gray-200 opacity-60'
                  }`}
                >
                  <div className="p-3 sm:p-6">
                    <div className="flex items-start justify-between gap-2 sm:gap-3">
                      <div className="flex-1 min-w-0">
                        {/* Badges row */}
                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                          <div className="flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md text-xs bg-blue-50">
                            <Repeat className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-500" />
                            <span className="font-medium text-blue-700">Recurring</span>
                          </div>
                          {rb.isActive ? (
                            <span className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3" />
                              <span className="hidden sm:inline">Active</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              <XCircle className="w-3 h-3" />
                              <span className="hidden sm:inline">Paused</span>
                            </span>
                          )}
                          {activeExceptions > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              {activeExceptions} skipped date{activeExceptions > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        {/* Title */}
                        <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1.5 sm:mb-2">
                          {rb.title || 'Recurring Booking'}
                        </h3>

                        {/* Pattern description */}
                        <div className="text-sm text-primary-700 font-medium mb-2">
                          {formatRecurringPattern(rb)}
                        </div>

                        <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                          {room && (
                            <div className="flex items-center gap-1 sm:gap-1.5">
                              <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                              <span className="truncate max-w-[100px] sm:max-w-none">
                                {room.name}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-1 sm:gap-1.5">
                            <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                            <span>
                              {rb.startTime} - {rb.endTime}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-1.5">
                            <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                            <span>
                              From{' '}
                              {new Date(rb.startDate + 'T00:00:00').toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                              {rb.endDate
                                ? ` to ${new Date(rb.endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                                : ' (no end date)'}
                            </span>
                          </div>
                        </div>

                        {rb.notes && (
                          <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-gray-500 line-clamp-2">
                            {rb.notes}
                          </p>
                        )}

                        {/* Created by info */}
                        {rb.createdBy && (
                          <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-2">
                            <Shield className="w-3.5 h-3.5 text-blue-500" />
                            <span>
                              Created by{' '}
                              <span className="font-semibold text-gray-700">
                                {rb.createdBy.name}
                              </span>
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex-shrink-0 flex items-center gap-1 sm:gap-2">
                        <button
                          onClick={() => openEditRecurring(rb)}
                          className="px-3 py-1.5 text-sm font-medium rounded transition-colors bg-primary-50 text-primary-700 hover:bg-primary-100"
                          title="Edit recurring booking"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() =>
                            toggleRecurringMutation.mutate({ id: rb.id, isActive: !rb.isActive })
                          }
                          disabled={toggleRecurringMutation.isPending}
                          className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                            rb.isActive
                              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                          title={
                            rb.isActive ? 'Pause recurring booking' : 'Reactivate recurring booking'
                          }
                        >
                          {rb.isActive ? 'Pause' : 'Resume'}
                        </button>
                        {deletingRecurringId === rb.id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">Delete series?</span>
                            <button
                              onClick={() => {
                                deleteRecurringMutation.mutate(rb.id)
                                setDeletingRecurringId(null)
                              }}
                              disabled={deleteRecurringMutation.isPending}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => setDeletingRecurringId(null)}
                              className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 transition-colors"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeletingRecurringId(rb.id)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete recurring booking"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}

      {!loading && filter !== 'recurring' && filteredUnifiedBookings.length > 0 && (
        <div className="space-y-4">
          {filteredUnifiedBookings.map((booking) => {
            const room = getRoomDetails(booking.roomId)
            const start = formatDateTime(booking.startTime!)
            const end = formatDateTime(booking.endTime!)
            const isPast = new Date(booking.endTime!) < new Date()
            const canCancel =
              !isPast &&
              booking.status !== 'cancelled' &&
              booking.status !== 'completed' &&
              booking.status !== 'denied'
            const canDelete = booking.status !== 'cancelled' // Can delete past bookings too

            // Approval trail only - who booked is shown prominently on the card itself
            const getBookingFlowInfo = () => {
              if (booking.status === 'confirmed' && booking.approvedBy) {
                return (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-2">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                    <span>
                      Approved by {booking.approvedBy.name}
                      {booking.statusChangedAt &&
                        ` on ${new Date(booking.statusChangedAt).toLocaleDateString()}`}
                    </span>
                  </div>
                )
              } else if (booking.status === 'denied' && booking.approvedBy) {
                return (
                  <div className="flex items-center gap-1.5 text-xs text-red-600 mt-2">
                    <XCircle className="w-3.5 h-3.5" />
                    <span>
                      Denied by {booking.approvedBy.name}
                      {booking.statusNote && `: ${booking.statusNote}`}
                    </span>
                  </div>
                )
              }
              return null
            }

            // Get the original booking object for the detail modal
            const originalBooking = bookings.find((b: Booking) => b.id === booking.id)

            return (
              <div
                key={`booking-${booking.id}`}
                className={`bg-white rounded-lg border-2 shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${
                  booking.status === 'denied'
                    ? 'border-red-300 bg-red-50/30'
                    : booking.status === 'pending'
                      ? 'border-yellow-400 bg-yellow-50/30'
                      : isPast
                        ? 'opacity-60 border-gray-200'
                        : 'border-gray-200'
                }`}
                onClick={() => {
                  if (booking.type === 'recurring-occurrence' && booking.recurringBookingId) {
                    const rb = recurringBookings.find((r) => r.id === booking.recurringBookingId)
                    if (rb) {
                      openEditRecurring(rb)
                    }
                  } else if (originalBooking) {
                    setSelectedBookingForDetail(originalBooking)
                  }
                }}
              >
                {/* Pending status banner */}
                {booking.status === 'pending' && (
                  <div className="bg-yellow-100 border-b border-yellow-300 px-3 sm:px-4 py-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-medium text-yellow-800">
                      Awaiting approval
                    </span>
                  </div>
                )}
                <div className="p-3 sm:p-6">
                  <div className="flex items-start justify-between gap-2 sm:gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Badges row - wraps nicely on mobile */}
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                        <div
                          className={`flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md text-xs ${
                            booking.type === 'recurring-occurrence'
                              ? 'bg-blue-50'
                              : booking.bookingSource === 'admin_direct'
                                ? 'bg-blue-50'
                                : 'bg-gray-100'
                          }`}
                        >
                          {booking.type === 'recurring-occurrence' ? (
                            <>
                              <Repeat className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-500" />
                              <span className="font-medium text-blue-700">Recurring</span>
                            </>
                          ) : booking.bookingSource === 'admin_direct' ? (
                            <>
                              <Shield className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-500" />
                              <span className="font-medium text-blue-700">Admin</span>
                            </>
                          ) : (
                            <>
                              <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-500" />
                              <span className="font-medium text-gray-600">One-time</span>
                            </>
                          )}
                        </div>
                        {booking.status === 'confirmed' && (
                          <span className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3" />
                            <span className="hidden sm:inline">Confirmed</span>
                          </span>
                        )}
                        {booking.status === 'pending' && (
                          <span className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <AlertCircle className="w-3 h-3" />
                            <span className="hidden sm:inline">Pending</span>
                          </span>
                        )}
                        {booking.status === 'denied' && (
                          <span className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <XCircle className="w-3 h-3" />
                            <span className="hidden sm:inline">Denied</span>
                          </span>
                        )}
                        {/* Payment badge */}
                        {booking.totalPrice &&
                          booking.totalPrice > 0 &&
                          booking.paymentStatus &&
                          booking.paymentStatus !== 'not_required' &&
                          (booking.paymentStatus === 'pending' ? (
                            <span className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              <CreditCard className="w-3 h-3" />
                              <span className="hidden sm:inline">Pay</span> £
                              {Number(booking.totalPrice).toFixed(2)}
                            </span>
                          ) : booking.paymentStatus === 'refunded' ? (
                            <span className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                              <CreditCard className="w-3 h-3" />
                              <span className="hidden sm:inline">Refunded</span>
                            </span>
                          ) : booking.paymentStatus === 'paid' ? (
                            <span className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                              <CreditCard className="w-3 h-3" />
                              <span className="hidden sm:inline">Paid</span>
                            </span>
                          ) : null)}
                      </div>

                      {/* Title */}
                      <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1.5 sm:mb-2 line-clamp-2">
                        {booking.title}
                      </h3>

                      {/* Who booked - the key detail, so it stays visible on mobile too */}
                      {(booking.bookedBy || booking.bookingSource === 'admin_direct') && (
                        <div className="flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-gray-600 mb-1.5 sm:mb-2">
                          <User className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                          <span>
                            Booked by{' '}
                            <span className="font-bold text-gray-900">
                              {booking.bookedBy?.name || 'Admin'}
                            </span>
                          </span>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                        {room && (
                          <div className="flex items-center gap-1 sm:gap-1.5">
                            <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                            <span className="truncate max-w-[100px] sm:max-w-none font-semibold text-gray-900">
                              {room.name}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-1 sm:gap-1.5">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                          <span className="font-semibold text-gray-900">{start.date}</span>
                        </div>
                        <div className="flex items-center gap-1 sm:gap-1.5">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                          <span className="font-semibold text-gray-900">
                            {start.time} - {end.time}
                          </span>
                        </div>
                      </div>

                      {booking.notes && (
                        <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-gray-500 line-clamp-2">
                          {booking.notes}
                        </p>
                      )}

                      {/* Booking flow/transparency info - hide on mobile */}
                      <div className="hidden sm:block">{getBookingFlowInfo()}</div>
                    </div>

                    <div
                      className="flex-shrink-0 flex items-center gap-1 sm:gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Approval actions for admins/approvers on pending bookings */}
                      {canApprove &&
                        booking.status === 'pending' &&
                        (denyingId === booking.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={denyReason}
                              onChange={(e) => setDenyReason(e.target.value)}
                              placeholder="Reason (optional)"
                              className="px-2 py-1 text-sm border border-gray-300 rounded w-40"
                            />
                            <button
                              onClick={() =>
                                denyMutation.mutate({
                                  bookingId: booking.id,
                                  reason: denyReason || undefined,
                                })
                              }
                              disabled={denyMutation.isPending}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                            >
                              Deny
                            </button>
                            <button
                              onClick={() => {
                                setDenyingId(null)
                                setDenyReason('')
                              }}
                              className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : approvingId === booking.id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">Approve this booking?</span>
                            <button
                              onClick={() => approveMutation.mutate(booking.id)}
                              disabled={approveMutation.isPending}
                              className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setApprovingId(null)}
                              className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : cancellingId === booking.id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">Delete request?</span>
                            <button
                              onClick={() =>
                                cancelMutation.mutate({ bookingId: booking.id, isPending: true })
                              }
                              disabled={cancelMutation.isPending}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                            >
                              Yes, Delete
                            </button>
                            <button
                              onClick={() => setCancellingId(null)}
                              className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 transition-colors"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => setApprovingId(booking.id)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors"
                              title="Approve booking"
                            >
                              <Check className="w-4 h-4" />
                              Approve
                            </button>
                            <button
                              onClick={() => setDenyingId(booking.id)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 transition-colors"
                              title="Deny booking"
                            >
                              <Ban className="w-4 h-4" />
                              Deny
                            </button>
                            <button
                              onClick={() => setCancellingId(booking.id)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-white text-red-700 border border-red-200 text-sm font-medium rounded hover:bg-red-50 transition-colors"
                              title="Delete this pending request"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </>
                        ))}

                      {/* Regular edit/cancel actions - show when not a pending booking being approved */}
                      {!(canApprove && booking.status === 'pending') &&
                        (cancellingId === booking.id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">
                              {booking.status === 'pending' ? 'Cancel request?' : 'Cancel?'}
                            </span>
                            <button
                              onClick={() =>
                                cancelMutation.mutate({
                                  bookingId: booking.id,
                                  isPending: booking.status === 'pending',
                                })
                              }
                              disabled={cancelMutation.isPending}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => setCancellingId(null)}
                              className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 transition-colors"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <>
                            {/* Only show edit/cancel for own bookings (or admins/approvers) */}
                            {(!isGuest || (!booking.isRedacted && booking.userId === user?.id)) && (
                              <>
                                <button
                                  onClick={() => {
                                    const orig = bookings.find((b: Booking) => b.id === booking.id)
                                    if (orig) openEditBooking(orig)
                                  }}
                                  className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                                  title="Edit booking"
                                >
                                  <Pencil className="w-5 h-5" />
                                </button>
                                {(canCancel || canDelete) && (
                                  <button
                                    onClick={() => setCancellingId(booking.id)}
                                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                    title={isPast ? 'Delete booking' : 'Cancel booking'}
                                  >
                                    <X className="w-5 h-5" />
                                  </button>
                                )}
                              </>
                            )}
                          </>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Edit One-time Booking Modal */}
      {editingBooking && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setEditingBooking(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Edit Booking</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={editBookingForm.title}
                    onChange={(e) =>
                      setEditBookingForm({ ...editBookingForm, title: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Booking title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={editBookingForm.date}
                    onChange={(e) =>
                      setEditBookingForm({ ...editBookingForm, date: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={editBookingForm.startTime}
                      onChange={(e) =>
                        setEditBookingForm({ ...editBookingForm, startTime: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                    <input
                      type="time"
                      value={editBookingForm.endTime}
                      onChange={(e) =>
                        setEditBookingForm({ ...editBookingForm, endTime: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={editBookingForm.notes}
                    onChange={(e) =>
                      setEditBookingForm({ ...editBookingForm, notes: e.target.value })
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Optional notes"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setEditingBooking(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveBooking}
                  disabled={updateBookingMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {updateBookingMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Booking Detail Modal */}
      {selectedBookingForDetail && (
        <BookingDetailModal
          booking={selectedBookingForDetail}
          room={rooms.find((r) => r.id === selectedBookingForDetail.roomId)}
          onClose={() => setSelectedBookingForDetail(null)}
          onUpdate={() => {
            queryClient.invalidateQueries({ queryKey: ['bookings'] })
          }}
        />
      )}

      {/* Edit Recurring Booking Modal */}
      {editingRecurring && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setEditingRecurring(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Repeat className="w-5 h-5 text-primary-600" />
                  Edit Recurring Booking
                </h2>
                <button
                  onClick={() => setEditingRecurring(null)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Pattern (read-only) */}
              <div className="bg-gray-50 rounded-lg px-3 py-2 mb-4">
                <div className="text-xs text-gray-500">Pattern</div>
                <div className="font-medium text-sm">
                  {formatRecurringPattern(editingRecurring)}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={recurringEditForm.title}
                    onChange={(e) =>
                      setRecurringEditForm({ ...recurringEditForm, title: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Booking title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Room</label>
                  <select
                    value={recurringEditForm.roomId}
                    onChange={(e) =>
                      setRecurringEditForm({ ...recurringEditForm, roomId: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {rooms.map((room) => (
                      <option key={room.id} value={room.id}>
                        {room.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={recurringEditForm.startTime}
                      onChange={(e) =>
                        setRecurringEditForm({ ...recurringEditForm, startTime: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                    <input
                      type="time"
                      value={recurringEditForm.endTime}
                      onChange={(e) =>
                        setRecurringEditForm({ ...recurringEditForm, endTime: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={recurringEditForm.endDate}
                    onChange={(e) =>
                      setRecurringEditForm({ ...recurringEditForm, endDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  {recurringEditForm.endDate && (
                    <button
                      onClick={() => setRecurringEditForm({ ...recurringEditForm, endDate: '' })}
                      className="text-xs text-gray-500 hover:text-gray-700 mt-1"
                    >
                      Clear end date (make indefinite)
                    </button>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={recurringEditForm.notes}
                    onChange={(e) =>
                      setRecurringEditForm({ ...recurringEditForm, notes: e.target.value })
                    }
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Optional notes"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Active</label>
                  <button
                    onClick={() =>
                      setRecurringEditForm({
                        ...recurringEditForm,
                        isActive: !recurringEditForm.isActive,
                      })
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      recurringEditForm.isActive ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        recurringEditForm.isActive ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setEditingRecurring(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    updateRecurringMutation.mutate({
                      id: editingRecurring.id,
                      data: {
                        title: recurringEditForm.title || undefined,
                        notes: recurringEditForm.notes || undefined,
                        startTime: recurringEditForm.startTime,
                        endTime: recurringEditForm.endTime,
                        roomId: recurringEditForm.roomId,
                        endDate: recurringEditForm.endDate || null,
                        isActive: recurringEditForm.isActive,
                      },
                    })
                  }}
                  disabled={updateRecurringMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {updateRecurringMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  )
}
